import type {
  Ability,
  ComfortBand,
  ComfortVerdict,
  SessionHour,
  SessionPreferences,
} from "./types";
import {
  personalFitContext,
  prefAbility,
  type EquipmentRange,
  type FoilBoardSetup,
  type FoilSetup,
  type SupPersonalFitContext,
} from "./preferences";
import { bodyboardSteepnessFactor } from "../conditions/bodyboard-scoring";
import type { ConditionSafetyState } from "../conditions/safety-notes";
import { foilEnergyBase } from "../conditions/foil-scoring-v5";
import {
  downwindEnergyBase,
  downwindSeaState,
  downwindSwellRideWeight,
} from "../conditions/downwind-foil-scoring";
import { assessSwimChop } from "../conditions/swim-analysis";

/** Day-level / spot-level context the per-hour comfort gate and explanations need. */
export type SessionContext = {
  waterStatus: "clear" | "caution" | "poor" | "unknown";
  /** Spot's beach-facing bearing (degrees), for wind-quality phrasing. */
  beachFacingDeg?: number | null;
  /** Sport-specific safety notes supplied by stored condition score rows. */
  safetyNotes?: string[];
  /** Canonical stored safety state. Missing means unavailable, never clear. */
  safetyState?: ConditionSafetyState;
};

const KT = 1.852; // 1 knot in km/h
const kt = (kmh: number) => kmh / KT;

const clampFit = (f: number) => Math.max(0.4, Math.min(1.2, f));

/** Gaussian preference fit: 1.2 on target, decaying with distance, floored. */
function targetFit(value: number, target: number, spread: number): number {
  return clampFit(1.2 * Math.exp(-(((value - target) / spread) ** 2)));
}

function bandFor(value: number, floor: number, ceiling: number): ComfortBand {
  if (value > ceiling) return "too-strong";
  if (value < floor) return "too-weak";
  return "in-range";
}

function timingOk(hour: number, timing: string): boolean {
  switch (timing) {
    case "dawn":
      return hour <= 8;
    case "morning":
      return hour >= 6 && hour < 12;
    case "afternoon-evening":
      return hour >= 15;
    case "morning-glassoff":
      return hour >= 5 && hour < 11;
    case "afternoon-thermals":
      return hour >= 12 && hour < 19;
    case "any":
    default:
      return true;
  }
}

type Core = {
  band: ComfortBand;
  fit: number;
  tempOk: boolean;
  qualityOk: boolean;
  intensityLabel: string; // excludedBy when band is out of range
};

// ---------------------------------------------------------------------------
// Surf / Bodyboarding (wave-driven)
// ---------------------------------------------------------------------------

const SURF_CEIL: Record<Ability, number> = { beginner: 0.9, intermediate: 1.5, advanced: 2.5, expert: 99 };
const SURF_FLOOR: Record<Ability, number> = { beginner: 0.1, intermediate: 0.3, advanced: 0.5, expert: 0.8 };
const BOARD_FLOOR_MULT: Record<string, number> = { longboard: 0.6, funboard: 1.0, shortboard: 1.4 };
const BOARD_CEIL_MULT: Record<string, number> = { longboard: 0.9, funboard: 1.0, shortboard: 1.1 };

function surfCore(prefs: SessionPreferences, h: SessionHour, ability: Ability): Core {
  const wave = h.waveHeightM ?? 0;
  const board = prefs.values.board ?? "funboard";
  const ceiling = SURF_CEIL[ability] * (BOARD_CEIL_MULT[board] ?? 1);
  const floor = SURF_FLOOR[ability] * (BOARD_FLOOR_MULT[board] ?? 1);
  const band = bandFor(wave, floor, ceiling);

  let fit = 1;
  if (prefs.values.crowd === "quieter") {
    if (h.hour <= 8 || h.hour >= 18) fit *= 1.05;
    else if (h.hour >= 10 && h.hour <= 16) fit *= 0.92;
    fit = clampFit(fit);
  }

  return { band, fit, tempOk: true, qualityOk: true, intensityLabel: "wave size" };
}

function bodyboardingCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability
): Core {
  const profile = personalFitContext(prefs);
  const preference = profile?.kind === "bodyboarding" ? profile.wavePreference : "unknown";
  const legacy = surfCore(prefs, h, ability);
  if (preference === "unknown" || preference === "mellow" || preference === "shorebreak") {
    return legacy;
  }

  const wave = h.waveHeightM ?? 0;
  const period = h.swellPeriodS ?? 0;
  // BODY-B8 (1.2m at 14s) is the executed mellow twin in the audit. A hollow
  // preference asks for more punch than that twin, using the canonical scorer's
  // own steepness curve rather than a second planner-only height threshold.
  const mellowTwinSteepness = bodyboardSteepnessFactor(1.2, 14);
  const lacksPunch = bodyboardSteepnessFactor(wave, period) <= mellowTwinSteepness;
  return lacksPunch
    ? { ...legacy, band: "too-weak", intensityLabel: "bodyboarding punch" }
    : legacy;
}

// ---------------------------------------------------------------------------
// Wind (windsurf / kitesurf)
// ---------------------------------------------------------------------------

const WIND_FLOOR_KT: Record<Ability, number> = { beginner: 14, intermediate: 12, advanced: 10, expert: 9 };
const WIND_CEIL_KT: Record<Ability, number> = { beginner: 22, intermediate: 28, advanced: 35, expert: 45 };
const WIND_RANGE_TARGET_KT: Record<string, number> = { light: 14, moderate: 19, strong: 26, storm: 34 };
const WIND_EQUIPMENT_TARGET_KT: Partial<Record<EquipmentRange, number>> = {
  "light-wind": WIND_RANGE_TARGET_KT.light,
  "high-wind": WIND_RANGE_TARGET_KT.strong,
};

function windCore(prefs: SessionPreferences, h: SessionHour, ability: Ability): Core {
  const profile = personalFitContext(prefs);
  const w = kt(h.windSpeedKmh);
  const band = bandFor(w, WIND_FLOOR_KT[ability], WIND_CEIL_KT[ability]);
  const windPreference = profile?.kind === "wind" ? profile.windPreference : "moderate";
  const equipmentRange = profile?.kind === "wind" ? profile.equipmentRange : "unknown";
  // Equipment uses the already-declared light and strong Wind targets. Unknown
  // and all-round preserve the selected preferred-wind target.
  const target = WIND_EQUIPMENT_TARGET_KT[equipmentRange]
    ?? WIND_RANGE_TARGET_KT[windPreference]
    ?? 19;
  const fit = targetFit(w, target, 5);
  return {
    band,
    fit,
    tempOk: true,
    qualityOk: true,
    intensityLabel:
      profile?.kind === "wind" && profile.equipmentRange !== "unknown"
        ? "ability wind control range"
        : "wind",
  };
}

// ---------------------------------------------------------------------------
// Foil (wing / kite / surf / pump / downwind)
// ---------------------------------------------------------------------------

const FOIL_WIND_FLOOR_KT: Record<Ability, number> = { beginner: 12, intermediate: 10, advanced: 8, expert: 7 };
const FOIL_WIND_CEIL_KT: Record<Ability, number> = { beginner: 24, intermediate: 30, advanced: 38, expert: 48 };
const FOIL_WIND_TARGET_KT: Record<string, number> = { light: 11, moderate: 17, strong: 24 };
const FOIL_SWELL_FLOOR: Record<Ability, number> = { beginner: 0.3, intermediate: 0.4, advanced: 0.5, expert: 0.6 };
const FOIL_SWELL_CEIL: Record<Ability, number> = { beginner: 1.2, intermediate: 1.8, advanced: 2.5, expert: 3.5 };

function legacyFoilCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  mode: "prone" | "downwind" | "wing"
): Core {
  if (mode === "wing" || mode === "downwind") {
    const wind = kt(h.windSpeedKmh);
    const floor = FOIL_WIND_FLOOR_KT[ability] + (mode === "downwind" ? 4 : 0);
    return {
      band: bandFor(wind, floor, FOIL_WIND_CEIL_KT[ability]),
      fit: targetFit(wind, FOIL_WIND_TARGET_KT[prefs.values.windPref] ?? 17, 5),
      tempOk: true,
      qualityOk: true,
      intensityLabel: "wind",
    };
  }

  const swell = h.waveHeightM ?? 0;
  let band = bandFor(swell, FOIL_SWELL_FLOOR[ability], FOIL_SWELL_CEIL[ability]);
  if (kt(h.windSpeedKmh) > 20) band = "too-strong";
  const target = prefs.values.swellPref === "moderate" ? 1.5 : prefs.values.swellPref === "none" ? 0.4 : 0.8;
  return {
    band,
    fit: targetFit(swell, target, 0.6),
    tempOk: true,
    qualityOk: true,
    intensityLabel: "swell",
  };
}

function proneFoilCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  _boardSetup: FoilBoardSetup,
  foilSetup: FoilSetup,
  pumpingAbility: "unknown" | "learning" | "competent" | "expert"
): Core {
  const swell = h.waveHeightM ?? 0;
  const legacy = legacyFoilCore(prefs, h, ability, "prone");
  if (legacy.band === "too-strong") return legacy;
  if (foilEnergyBase(swell) === 0) {
    return { ...legacy, band: "too-weak", intensityLabel: "prone pumpable energy" };
  }

  // PRONE-P7 is the audited 0.3m at 12s niche case. It is viable for an
  // advanced/expert rider, or for a competent pumper selecting low-speed lift.
  const isMarginalNiche = swell <= 0.3 && (h.swellPeriodS ?? 0) >= 12;
  const nicheCapable =
    ability === "advanced" ||
    ability === "expert" ||
    pumpingAbility === "expert" ||
    (pumpingAbility === "competent" && foilSetup === "low-speed");
  if (isMarginalNiche && !nicheCapable) {
    return { ...legacy, band: "too-weak", intensityLabel: "prone pumping progression" };
  }
  if (isMarginalNiche && nicheCapable) return { ...legacy, band: "in-range" };
  return legacy;
}

function downwindFoilCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  _boardSetup: FoilBoardSetup,
  _foilSetup: FoilSetup
): Core {
  const legacy = legacyFoilCore(prefs, h, ability, "downwind");
  const windWave = h.windWaveHeightM;
  if (windWave === null || windWave === undefined) return legacy;

  const swell = h.waveHeightM ?? 0;
  const rideWeight = downwindSwellRideWeight(
    h.windSpeedKmh,
    h.windDirDeg,
    h.swellDirDeg
  );
  if (rideWeight === 0) {
    return { ...legacy, band: "too-weak", intensityLabel: "wind and swell alignment" };
  }
  const { rideableCombinedSeaM } = downwindSeaState(
    swell,
    windWave,
    1,
    h.windSpeedKmh,
    h.windDirDeg,
    h.swellDirDeg
  );
  if (downwindEnergyBase(rideableCombinedSeaM) === 0) {
    return { ...legacy, band: "too-weak", intensityLabel: "downwind combined bump energy" };
  }
  return { ...legacy, band: "in-range", intensityLabel: "downwind combined bump energy" };
}

function wingFoilCore(
  h: SessionHour,
  ability: Ability,
  _boardSetup: FoilBoardSetup,
  _foilSetup: FoilSetup,
  wingRange: EquipmentRange,
  windPreference: "light" | "moderate" | "strong"
): Core {
  const wind = kt(h.windSpeedKmh);
  const legacyBand = bandFor(wind, FOIL_WIND_FLOOR_KT[ability], FOIL_WIND_CEIL_KT[ability]);
  let band = legacyBand;
  // These two explicit ranges are anchored to WING-G7 (15km/h light-wind
  // equipment) and WING-G8 (46km/h high-wind equipment). Unknown and all-round
  // retain the pre-task ability envelope.
  if (wingRange === "light-wind") {
    band = h.windSpeedKmh < 10
      ? "too-weak"
      : h.windSpeedKmh <= 15
        ? "in-range"
        : h.windSpeedKmh > 40
          ? "too-strong"
          : legacyBand;
  } else if (wingRange === "high-wind") {
    band = h.windSpeedKmh < 20 ? "too-weak" : legacyBand;
  }

  return {
    band,
    fit: targetFit(wind, FOIL_WIND_TARGET_KT[windPreference] ?? 17, 5),
    tempOk: true,
    qualityOk: true,
    intensityLabel: "wing equipment wind range",
  };
}

function foilCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability
): Core {
  const profile = personalFitContext(prefs);
  if (profile?.kind !== "foil") {
    return legacyFoilCore(prefs, h, ability, "wing");
  }
  if (profile.mode === "prone") {
    if (
      profile.boardSetup === "unknown" &&
      profile.foilSetup === "unknown" &&
      profile.pumpingAbility === "unknown"
    ) {
      return legacyFoilCore(prefs, h, ability, "prone");
    }
    return proneFoilCore(
      prefs,
      h,
      ability,
      profile.boardSetup,
      profile.foilSetup,
      profile.pumpingAbility
    );
  }
  if (profile.mode === "downwind") {
    if (profile.boardSetup === "unknown" && profile.foilSetup === "unknown") {
      return legacyFoilCore(prefs, h, ability, "downwind");
    }
    return downwindFoilCore(
      prefs,
      h,
      ability,
      profile.boardSetup,
      profile.foilSetup
    );
  }
  if (
    profile.boardSetup === "unknown" &&
    profile.foilSetup === "unknown" &&
    profile.wingRange === "unknown"
  ) {
    return legacyFoilCore(prefs, h, ability, "wing");
  }
  return wingFoilCore(
    h,
    ability,
    profile.boardSetup,
    profile.foilSetup,
    profile.wingRange ?? "unknown",
    "windPreference" in profile ? profile.windPreference : "moderate"
  );
}

// ---------------------------------------------------------------------------
// SUP (touring / surf / fitness / downwind)
// ---------------------------------------------------------------------------

export type SupFitResult = Pick<
  ComfortVerdict,
  "fitAvailability" | "acceptable" | "fitMultiplier" | "excludedBy"
>;

type CompleteTouringSupContext = SupPersonalFitContext & {
  profile: "sup-touring";
  ability: Exclude<SupPersonalFitContext["ability"], "unknown">;
  boardType: Exclude<SupPersonalFitContext["boardType"], "unknown">;
  plannedDistanceKm: number;
  comfortableDistanceKm: number;
};

type CompleteSurfSupContext = SupPersonalFitContext & {
  profile: "sup-surf";
  boardType: Exclude<SupPersonalFitContext["boardType"], "unknown">;
  boardVolumeL: number;
  riderMassKg: number;
  surfCompetence: Exclude<SupPersonalFitContext["surfCompetence"], "unknown">;
  waveComfortMaxM: number;
  leash: Exclude<SupPersonalFitContext["leash"], "unknown">;
  safetyKit: Exclude<SupPersonalFitContext["safetyKit"], "unknown">;
  launchCompetence: Exclude<SupPersonalFitContext["launchCompetence"], "unknown">;
};

type CompleteDownwindSupContext = SupPersonalFitContext & {
  profile: "sup-downwind";
  boardType: Exclude<SupPersonalFitContext["boardType"], "unknown">;
  boardLengthFt: number;
  downwindCompetence: Exclude<SupPersonalFitContext["downwindCompetence"], "unknown">;
  plannedDistanceKm: number;
  comfortableDistanceKm: number;
  communications: Exclude<SupPersonalFitContext["communications"], "unknown">;
  selfRescue: Exclude<SupPersonalFitContext["selfRescue"], "unknown">;
  routePlan: Exclude<SupPersonalFitContext["routePlan"], "unknown">;
  recoveryPlan: Exclude<SupPersonalFitContext["recoveryPlan"], "unknown">;
};

type CompleteSupPersonalFitContext =
  | CompleteTouringSupContext
  | CompleteSurfSupContext
  | CompleteDownwindSupContext;

const missingSupFit = (detail: string): SupFitResult => ({
  fitAvailability: "unavailable",
  acceptable: false,
  fitMultiplier: 0,
  excludedBy: `Personal suitability unavailable: ${detail}`,
});

const excludedSupFit = (reason: string): SupFitResult => ({
  fitAvailability: "available",
  acceptable: false,
  fitMultiplier: 0,
  excludedBy: reason,
});

const availableSupFit = (factors: readonly number[]): SupFitResult => ({
  fitAvailability: "available",
  acceptable: true,
  fitMultiplier: geometricSupPersonalFit(factors),
  excludedBy: null,
});

function finitePositive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/** Independent Beta suitability factors combine without penalising longer profiles. */
export function geometricSupPersonalFit(factors: readonly number[]): number {
  if (
    factors.length === 0 ||
    factors.some((factor) => !Number.isFinite(factor) || factor <= 0)
  ) {
    throw new Error("personal-fit factors must be finite and positive");
  }
  const raw = Math.exp(
    factors.reduce((sum, factor) => sum + Math.log(factor), 0) / factors.length,
  );
  return Math.round(Math.max(0.4, Math.min(1.2, raw)) * 1_000) / 1_000;
}

function distanceFactor(
  plannedDistanceKm: number,
  comfortableDistanceKm: number,
  exclusion: string,
): number | SupFitResult {
  if (plannedDistanceKm > comfortableDistanceKm) return excludedSupFit(exclusion);
  const half = comfortableDistanceKm * 0.5;
  const fourFifths = comfortableDistanceKm * 0.8;
  if (plannedDistanceKm <= half) return 1.05;
  if (plannedDistanceKm <= fourFifths) return 1.02;
  return 1;
}

function readinessFactor(value: "planned" | "confirmed"): number {
  return value === "planned" ? 0.95 : 1;
}

/**
 * Validate profile-owned preference evidence before any hourly fit or
 * exclusion is considered. A null result means the required preference bag is
 * complete; it does not assert that a particular forecast hour is suitable.
 */
export function requiredSupPersonalFitEvidence(
  context: SupPersonalFitContext,
): SupFitResult | null {
  if (context.profile === "sup-touring") {
    if (context.ability === "unknown") return missingSupFit("ability not confirmed");
    if (context.boardType === "unknown") return missingSupFit("board type not confirmed");
    if (!finitePositive(context.plannedDistanceKm)) {
      return missingSupFit("planned distance not confirmed");
    }
    if (!finitePositive(context.comfortableDistanceKm)) {
      return missingSupFit("comfortable distance not confirmed");
    }
    return null;
  }

  if (context.profile === "sup-surf") {
    if (context.surfCompetence === "unknown") {
      return missingSupFit("surf competence not confirmed");
    }
    if (context.boardType === "unknown") return missingSupFit("board type not confirmed");
    if (!finitePositive(context.boardVolumeL)) return missingSupFit("board volume not confirmed");
    if (!finitePositive(context.riderMassKg)) return missingSupFit("rider mass not confirmed");
    if (!finitePositive(context.waveComfortMaxM)) return missingSupFit("wave comfort not confirmed");
    if (context.leash === "unknown") return missingSupFit("leash not confirmed");
    if (context.safetyKit === "unknown") return missingSupFit("safety kit not confirmed");
    if (context.launchCompetence === "unknown") {
      return missingSupFit("launch competence not confirmed");
    }
    return null;
  }

  if (context.boardType === "unknown") return missingSupFit("board type not confirmed");
  if (!finitePositive(context.boardLengthFt)) return missingSupFit("board length not confirmed");
  if (context.downwindCompetence === "unknown") {
    return missingSupFit("Downwind competence not confirmed");
  }
  if (!finitePositive(context.plannedDistanceKm)) {
    return missingSupFit("planned distance not confirmed");
  }
  if (!finitePositive(context.comfortableDistanceKm)) {
    return missingSupFit("comfortable distance not confirmed");
  }
  if (context.communications === "unknown") {
    return missingSupFit("communications not confirmed");
  }
  if (context.selfRescue === "unknown") {
    return missingSupFit("self-rescue competence not confirmed");
  }
  if (context.routePlan === "unknown") return missingSupFit("route plan not confirmed");
  if (context.recoveryPlan === "unknown") {
    return missingSupFit("recovery plan not confirmed");
  }
  return null;
}

/**
 * Broad Beta suitability bands, not a qualification or safety clearance.
 * Board-volume references:
 * https://www.blueplanetsurf.com/service/choosing-a-board/
 * https://star-board.com/products/2026-pro-paddleboard
 */
export function calculateSupPersonalFit(
  context: SupPersonalFitContext,
  hour: SessionHour,
): SupFitResult {
  const missingEvidence = requiredSupPersonalFitEvidence(context);
  if (missingEvidence) return missingEvidence;
  // The validator above proves every profile-owned required field before any
  // exclusion or arithmetic. Keep the cast at this single audited boundary.
  const complete = context as CompleteSupPersonalFitContext;

  if (complete.profile === "sup-touring") {
    const ability = {
      beginner: 0.9,
      intermediate: 1,
      advanced: 1.05,
      expert: 1.05,
    }[complete.ability];
    const board = {
      "all-round": 1,
      touring: 1.05,
      surf: 0.8,
      downwind: 0.85,
    }[complete.boardType];
    const distance = distanceFactor(
      complete.plannedDistanceKm,
      complete.comfortableDistanceKm,
      "planned distance exceeds declared comfort",
    );
    return typeof distance === "number"
      ? availableSupFit([ability, board, distance])
      : distance;
  }

  if (complete.profile === "sup-surf") {
    if (
      hour.waveHeightM === null ||
      !Number.isFinite(hour.waveHeightM) ||
      hour.waveHeightM < 0
    ) {
      return missingSupFit("rideable wave height not confirmed");
    }

    if (complete.boardType === "touring" || complete.boardType === "downwind") {
      return excludedSupFit("Surf-specific board required");
    }
    const competence = { learning: 0.9, competent: 1, advanced: 1.05 }[
      complete.surfCompetence
    ];
    const board = complete.boardType === "surf" ? 1.05 : 0.85;
    const minimum = { learning: 1.8, competent: 1.4, advanced: 1.1 }[
      complete.surfCompetence
    ];
    const volumeRatio = complete.boardVolumeL / complete.riderMassKg;
    if (volumeRatio < minimum * 0.85) {
      return excludedSupFit("insufficient board float for declared competence");
    }
    const volume = volumeRatio >= minimum ? 1.05 : 0.85;

    const competenceCap = { learning: 0.5, competent: 1.2, advanced: 1.8 }[
      complete.surfCompetence
    ];
    const effectiveComfort = Math.min(competenceCap, complete.waveComfortMaxM);
    if (hour.waveHeightM > effectiveComfort) {
      return excludedSupFit("forecast wave exceeds declared comfort");
    }
    const halfComfort = effectiveComfort * 0.5;
    const fourFifthsComfort = effectiveComfort * 0.8;
    const wave = hour.waveHeightM <= halfComfort
      ? 1.05
      : hour.waveHeightM <= fourFifthsComfort
        ? 1.02
        : 1;
    const launch = { learning: 0.85, competent: 1, advanced: 1.05 }[
      complete.launchCompetence
    ];
    return availableSupFit([
      competence,
      board,
      volume,
      wave,
      readinessFactor(complete.leash),
      readinessFactor(complete.safetyKit),
      launch,
    ]);
  }

  if (complete.boardType !== "downwind" || complete.boardLengthFt < 12.5) {
    return excludedSupFit("dedicated 12 ft 6 in Downwind board required");
  }
  if (complete.downwindCompetence === "learning") {
    return excludedSupFit("experienced Downwind competence required");
  }
  if (complete.selfRescue === "learning") {
    return excludedSupFit("competent self-rescue required");
  }
  const distance = distanceFactor(
    complete.plannedDistanceKm,
    complete.comfortableDistanceKm,
    "route distance exceeds declared comfort",
  );
  if (typeof distance !== "number") return distance;
  return availableSupFit([
    1,
    complete.downwindCompetence === "advanced" ? 1.05 : 1,
    distance,
    readinessFactor(complete.communications),
    complete.selfRescue === "advanced" ? 1.05 : 1,
    readinessFactor(complete.routePlan),
    readinessFactor(complete.recoveryPlan),
  ]);
}

const SUP_WIND_CEIL_KMH: Record<string, number> = { calm: 12, light: 22, moderate: 32 };

function supCore(prefs: SessionPreferences, h: SessionHour, ability: Ability): Core {
  const style = prefs.values.style ?? "touring";
  const wind = h.windSpeedKmh;
  const wave = h.waveHeightM ?? 0;

  if (style === "downwind") {
    // wind-driven glide: needs wind, more is generally better
    const w = kt(wind);
    const band = bandFor(w, 12, 40);
    const fit = targetFit(wind, 34, 12);
    return { band, fit, tempOk: true, qualityOk: true, intensityLabel: "wind" };
  }

  if (style === "surf") {
    return surfCore(prefs, h, ability);
  }

  // touring / fitness: flat and calm is ideal, wind is the limiter
  const ceiling = SUP_WIND_CEIL_KMH[prefs.values.windTol] ?? 22;
  const waveCeil = prefs.values.waterState === "waves" ? 1.0 : prefs.values.waterState === "chop" ? 0.5 : 0.3;
  const band: ComfortBand = wind > ceiling || wave > waveCeil ? "too-strong" : "in-range";
  const fit = clampFit(1.2 - kt(wind) / 18);
  return { band, fit, tempOk: true, qualityOk: true, intensityLabel: "wind" };
}

// ---------------------------------------------------------------------------
// Open water swimming (chop-driven; temperature & quality are personal gates)
// ---------------------------------------------------------------------------

const SWIM_INFERRED_WIND_CEIL_KMH: Record<Ability, number> = {
  beginner: 14,
  intermediate: 20,
  advanced: 26,
  expert: 32,
};
const SWIM_WIND_PREFERENCE_CEIL_KMH: Record<string, number> = {
  calm: 14,
  light: 24,
};
// Planner-only suitability limits. They are conservative, monotonic personal
// envelopes rather than universal open-water safety thresholds.
const SWIM_CHOP_CEIL_M: Record<Ability, number> = {
  beginner: 0.25,
  intermediate: 0.45,
  advanced: 0.7,
  expert: 1,
};
const SWIM_MIN_TEMP: Record<string, number> = { warm: 18, moderate: 14, cold: -99 };

function swimCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  ctx: SessionContext,
): Core {
  const wind = h.windSpeedKmh;
  const chop = assessSwimChop({
    swellHeightM: h.waveHeightM ?? 0,
    windWaveHeightM: h.windWaveHeightM ?? null,
    swellPeriodS: h.swellPeriodS,
    windSpeedKmh: wind,
  });
  const exceedsMeasuredChop = chop.effectiveChopM > SWIM_CHOP_CEIL_M[ability];
  const exceedsInferredWind =
    chop.windProxyKmh !== null &&
    chop.windProxyKmh > Math.min(
      SWIM_INFERRED_WIND_CEIL_KMH[ability],
      SWIM_WIND_PREFERENCE_CEIL_KMH[prefs.values.windTol] ?? 24,
    );
  const band: ComfortBand = exceedsMeasuredChop || exceedsInferredWind
    ? "too-strong"
    : "in-range";
  const fit = chop.confidence === "full"
    ? clampFit(1.2 - chop.effectiveChopM * 0.6)
    : clampFit(1.2 - kt(wind) / 16 - chop.effectiveChopM * 0.3);

  const tempComfort = prefs.values.tempComfort ?? "moderate";
  const tempOk =
    h.waterTempC === null || h.waterTempC >= (SWIM_MIN_TEMP[tempComfort] ?? 14);

  const concern = prefs.values.waterQuality ?? "moderate";
  const status = ctx.waterStatus;
  const qualityOk =
    concern === "relaxed"
      ? true
      : concern === "high"
        ? status !== "poor" && status !== "caution"
        : status !== "poor";

  return {
    band,
    fit,
    tempOk,
    qualityOk,
    intensityLabel: chop.confidence === "full" ? "measured chop" : "inferred chop and wind",
  };
}

// ---------------------------------------------------------------------------
// Diving (personal context only; the canonical Diving score is never changed)
// ---------------------------------------------------------------------------

function divingCore(prefs: SessionPreferences, h: SessionHour): Core {
  const profile = personalFitContext(prefs);
  if (profile?.kind !== "diving") {
    return { band: "in-range", fit: 1, tempOk: true, qualityOk: true, intensityLabel: "dive context" };
  }

  let fit = 1;
  const visibility = h.underwaterVisibilityM;
  const lowVisibilityContext =
    profile.mode === "low-visibility" && profile.training === "low-visibility-trained";
  const advancedTraining = profile.training === "advanced";
  if (visibility !== null && visibility !== undefined) {
    const visibilityFit = clampFit(0.45 + visibility / 4);
    fit *= lowVisibilityContext
      ? Math.max(0.8, visibilityFit)
      : advancedTraining
        ? Math.max(0.7, visibilityFit)
        : visibilityFit;
  }

  const current = h.currentVelocityMs;
  const currentContext = profile.mode === "drift" && profile.training === "current-trained";
  if (current !== null && current !== undefined) {
    const currentFit = clampFit(1 - current * 0.5);
    // Direction is unavailable, so drift context can soften a personal-fit
    // penalty but can never turn current into a positive or a safety clearance.
    fit *= currentContext
      ? Math.max(0.8, currentFit)
      : advancedTraining
        ? Math.max(0.7, currentFit)
        : currentFit;
  }

  return {
    band: "in-range",
    fit: clampFit(fit),
    tempOk: true,
    qualityOk: true,
    intensityLabel: "dive context",
  };
}

// ---------------------------------------------------------------------------
// Sailing (dinghy / keelboat / catamaran)
// ---------------------------------------------------------------------------

const SAIL_CEIL_KT: Record<string, Record<Ability, number>> = {
  dinghy: { beginner: 16, intermediate: 22, advanced: 28, expert: 34 },
  keelboat: { beginner: 24, intermediate: 30, advanced: 36, expert: 42 },
  catamaran: { beginner: 20, intermediate: 26, advanced: 32, expert: 38 },
};
const SAIL_WIND_TARGET_KT: Record<string, number> = { light: 9, moderate: 15, fresh: 21, strong: 28 };
const SAIL_SEA_CEIL_M: Record<string, number> = { calm: 0.5, moderate: 1.2, rough: 2.5 };

function sailingCore(prefs: SessionPreferences, h: SessionHour, ability: Ability): Core {
  const boat = prefs.values.boat ?? "dinghy";
  const w = kt(h.windSpeedKmh);
  let ceiling = (SAIL_CEIL_KT[boat] ?? SAIL_CEIL_KT.dinghy)[ability];
  // dinghies are gust-sensitive: a gusty day tightens the ceiling
  if (boat === "dinghy" && h.windGustKmh && h.windSpeedKmh > 0) {
    const ratio = h.windGustKmh / h.windSpeedKmh;
    if (ratio > 1.5) ceiling -= 4;
  }
  const seaCeil = SAIL_SEA_CEIL_M[prefs.values.seaState] ?? 1.2;
  const wave = h.waveHeightM ?? 0;

  let band: ComfortBand = bandFor(w, 5, ceiling);
  if (band === "in-range" && wave > seaCeil) band = "too-strong";

  const fit = targetFit(w, SAIL_WIND_TARGET_KT[prefs.values.windRange] ?? 15, 5);
  return { band, fit, tempOk: true, qualityOk: true, intensityLabel: "wind" };
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

function sportCore(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  ctx: SessionContext
): Core {
  switch (prefs.sport) {
    case "surf":
      return surfCore(prefs, h, ability);
    case "bodyboarding":
      return bodyboardingCore(prefs, h, ability);
    case "wind":
      return windCore(prefs, h, ability);
    case "foil":
      return foilCore(prefs, h, ability);
    case "sup":
      return supCore(prefs, h, ability);
    case "swim":
      return swimCore(prefs, h, ability, ctx);
    case "diving":
      return divingCore(prefs, h);
    case "sailing":
      return sailingCore(prefs, h, ability);
    default:
      return { band: "in-range", fit: 1, tempOk: true, qualityOk: true, intensityLabel: "conditions" };
  }
}

export function comfortVerdict(
  prefs: SessionPreferences,
  h: SessionHour,
  ctx: SessionContext = { waterStatus: "unknown" }
): ComfortVerdict {
  const personalFit = personalFitContext(prefs);
  if (personalFit?.kind === "sup") {
    const fit = calculateSupPersonalFit(personalFit, h);
    return { ...fit, band: "in-range" };
  }
  const ability = prefAbility(prefs);
  const core = sportCore(prefs, h, ability, ctx);
  const timing = prefs.values.timing ?? "any";

  if (!timingOk(h.hour, timing)) {
    return { acceptable: false, band: core.band, fitMultiplier: core.fit, fitAvailability: "available", excludedBy: "timing" };
  }
  if (!core.tempOk) {
    return { acceptable: false, band: core.band, fitMultiplier: core.fit, fitAvailability: "available", excludedBy: "water temp" };
  }
  if (!core.qualityOk) {
    return { acceptable: false, band: core.band, fitMultiplier: core.fit, fitAvailability: "available", excludedBy: "water quality" };
  }
  if (core.band !== "in-range") {
    return { acceptable: false, band: core.band, fitMultiplier: core.fit, fitAvailability: "available", excludedBy: core.intensityLabel };
  }
  return { acceptable: true, band: "in-range", fitMultiplier: core.fit, fitAvailability: "available", excludedBy: null };
}

/**
 * The pre-gate core band for an arbitrary ability — used by the heatmap's
 * "by level" lens. Ignores the user's timing/temp/quality gates on purpose
 * (it answers "who do these conditions suit"), but stays equipment-aware
 * (the user's board/discipline still feed the band).
 */
export function sportBandFor(
  prefs: SessionPreferences,
  h: SessionHour,
  ability: Ability,
  ctx: SessionContext = { waterStatus: "unknown" }
): ComfortBand {
  const personalFit = personalFitContext(prefs);
  if (personalFit?.kind === "sup") return "in-range";
  return sportCore(prefs, h, ability, ctx).band;
}
